// "How it works" under the loop line of an agent's page: the guide's loop (§1, §2) drawn — the model inside the
// harness, the tool loop, the checks before the answer, budgets and runs around it. Each block opens its tab.
import { cn } from '@cortex/ui';
import * as React from 'react';

/** Whether the schema is open: closed each time an agent's page opens, it is there for when one asks. */
export function useSchemaOpen(): [boolean, () => void] {
  const [open, setOpen] = React.useState(false);
  return [open, () => setOpen((o) => !o)];
}

interface BlockProps {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  title: string;
  sub?: string;
  model?: boolean;
}

export function AgentSchema({
  ids,
  open,
  setTab,
  onClose,
}: {
  /** The tabs the page has: a block of a missing tab is drawn but opens nothing. */
  ids: string[];
  /** The open tab, outlined. */
  open: string;
  setTab(id: string): void;
  onClose(): void;
}) {
  const uid = React.useId().replace(/:/g, '');
  const arrow = `url(#${uid}-a)`;
  const loop = `url(#${uid}-l)`;
  const block = ({ id, x, y, w, h, title, sub, model = false }: BlockProps) => {
    const can = ids.includes(id);
    const go = () => can && setTab(id);
    return (
      <g
        role={can ? 'button' : undefined}
        tabIndex={can ? 0 : undefined}
        aria-label={can ? `Open ${title}` : undefined}
        data-testid={`agent-schema-${id}`}
        className={cn('outline-none', can ? 'cursor-pointer group' : '')}
        onClick={go}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            go();
          }
        }}
      >
        <rect
          x={x}
          y={y}
          width={w}
          height={h}
          rx={9}
          strokeWidth={id === open ? 2 : 1.2}
          className={cn(
            'transition-colors',
            model ? 'fill-page-bg stroke-page-muted/50' : 'fill-nav-active/10 stroke-nav-active/40',
            id === open ? 'stroke-nav-active' : '',
            can ? 'group-hover:stroke-nav-active group-focus-visible:stroke-nav-active' : '',
          )}
        />
        <text
          x={x + w / 2}
          y={sub ? y + h / 2 - 3 : y + h / 2 + 5}
          textAnchor="middle"
          className="fill-page-text text-[13px] font-semibold"
        >
          {title}
        </text>
        {sub ? (
          <text
            x={x + w / 2}
            y={y + h / 2 + 14}
            textAnchor="middle"
            className="fill-page-muted text-[11.5px]"
          >
            {sub}
          </text>
        ) : null}
      </g>
    );
  };
  const pill = (x: number, y: number, w: number, label: string) => (
    <g>
      <rect x={x} y={y} width={w} height={40} rx={20} className="fill-card-bg stroke-card-border" />
      <text x={x + w / 2} y={y + 24} textAnchor="middle" className="fill-page-text text-xs">
        {label}
      </text>
    </g>
  );
  const stroke = 'fill-none stroke-page-muted/70';
  const blue = 'fill-none stroke-nav-active';
  const label = 'fill-page-muted text-[11px]';
  return (
    <div
      className="mb-3 rounded-lg border border-card-border bg-card-bg px-3.5 pt-3 pb-2.5"
      data-testid="agent-schema"
    >
      <div className="flex items-baseline justify-between gap-3">
        <b className="text-sm text-page-text">How the agent works</b>
        <span className="flex items-center gap-2 text-xs text-page-muted">
          agent = model + harness · click a block to open its tab
          <button
            type="button"
            className="border-0 bg-transparent text-page-muted hover:text-page-text cursor-pointer text-base leading-none"
            aria-label="Close how it works"
            data-testid="agent-schema-close"
            onClick={onClose}
          >
            ✕
          </button>
        </span>
      </div>
      <svg
        viewBox="0 0 960 300"
        className="block h-auto w-full"
        role="img"
        aria-label="The agent's loop: the context goes to the model; the model calls tools and gets their results until its answer is final; the guardrails and verification check the answer; budgets can stop it at any step and runs record every step. All but the model is the harness."
      >
        <defs>
          <marker
            id={`${uid}-a`}
            viewBox="0 0 10 10"
            refX={9}
            refY={5}
            markerWidth={7}
            markerHeight={7}
            orient="auto"
          >
            <path d="M0 0L10 5L0 10z" className="fill-page-muted" />
          </marker>
          <marker
            id={`${uid}-l`}
            viewBox="0 0 10 10"
            refX={9}
            refY={5}
            markerWidth={7}
            markerHeight={7}
            orient="auto"
          >
            <path d="M0 0L10 5L0 10z" className="fill-nav-active" />
          </marker>
        </defs>
        <rect
          x={96}
          y={12}
          width={740}
          height={276}
          rx={14}
          strokeDasharray="5 4"
          className="fill-nav-active/[0.03] stroke-nav-active/40"
        />
        <text x={112} y={32} className="fill-page-muted text-[11px] font-semibold tracking-wider">
          HARNESS — ALL BUT THE MODEL
        </text>
        {pill(6, 122, 74, 'Question')}
        <path d="M80 142 H122" className={stroke} markerEnd={arrow} />
        {block({
          id: 'instructions',
          x: 124,
          y: 117,
          w: 132,
          h: 50,
          title: 'Context',
          sub: 'instructions · history',
        })}
        <path d="M256 142 H306" className={stroke} markerEnd={arrow} />
        {block({ id: 'model', x: 308, y: 117, w: 132, h: 50, title: 'Model', sub: 'thinks', model: true })}
        <path d="M420 117 C420 80 450 74 492 74" className={blue} markerEnd={loop} />
        <text x={400} y={88} textAnchor="end" className={label}>
          call a tool
        </text>
        {block({ id: 'tools', x: 494, y: 50, w: 168, h: 50, title: 'Tools', sub: 'may it? → call → result' })}
        <path d="M560 100 C560 132 470 138 442 138" className={blue} markerEnd={loop} />
        <text x={566} y={128} className={label}>
          result ↺ again
        </text>
        <path d="M440 156 C520 160 600 166 638 166" className={stroke} markerEnd={arrow} />
        <text x={520} y={182} textAnchor="middle" className={label}>
          final answer
        </text>
        {block({
          id: 'guardrails',
          x: 640,
          y: 141,
          w: 178,
          h: 50,
          title: 'Guardrails & Verification',
          sub: 'check the answer',
        })}
        <path d="M818 166 H866" className={stroke} markerEnd={arrow} />
        {pill(868, 146, 84, 'Answer')}
        {block({
          id: 'harness',
          x: 124,
          y: 222,
          w: 330,
          h: 50,
          title: 'Budgets',
          sub: 'can stop it at any step: steps, calls, tokens, time',
        })}
        {block({
          id: 'runs',
          x: 470,
          y: 222,
          w: 348,
          h: 50,
          title: 'Runs',
          sub: 'every step recorded, with its outcome',
        })}
      </svg>
      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-page-muted">
        <span className="inline-flex items-center gap-1.5">
          <i className="inline-block h-3 w-3 rounded-sm border border-nav-active/40 bg-nav-active/10" />
          harness — what we build
        </span>
        <span className="inline-flex items-center gap-1.5">
          <i className="inline-block h-3 w-3 rounded-sm border border-page-muted/50 bg-page-bg" />
          model — bought (Claude, GPT)
        </span>
        <span className="inline-flex items-center gap-1.5">
          <i className="inline-block h-3 w-3 rounded-sm bg-nav-active" />
          the loop: until the answer is final
        </span>
      </div>
    </div>
  );
}
